import React from "react"
import { makeStyles } from "@material-ui/core"
import { colors } from "../../utils/theme"
import Button from "../atoms/button/Button"

type MobileFeatureToggleRowButtonLabels = {
  enable: string
  disable: string
  enabling: string
  disabling: string
}

interface MobileFeatureToggleRowProps {
  label: string
  description: string
  enabled: boolean
  onSetEnabled: (nextEnabled: boolean) => void | Promise<void>
  isLoading?: boolean
  successMessage?: string | null
  errorMessage?: string | null
}
/**
 * Renders a single “feature toggle” row for the Client Mobile Settings screen.
 *
 * Shows the feature `label`, the current enabled/disabled status, a short
 * `description`, and an action button that toggles the feature on/off.
 *
 * @param label - Feature name shown in the left column.
 * @param description - Supporting text explaining what the toggle controls.
 * @param enabled - Current enabled state.
 * @param onSetEnabled - Callback invoked with the next enabled value when the
 * button is clicked. May return a promise.
 * @param isLoading - When true, disables the button and switches its label to
 * “Enabling…” / “Disabling…”.
 * @param errorMessage - Optional error feedback shown inline (announced with
 * `role="alert"`).
 */
export default function MobileFeatureToggleRow({
  label,
  description,
  enabled,
  onSetEnabled,
  isLoading = false,
  successMessage,
  errorMessage,
}: MobileFeatureToggleRowProps) {
  const classes = useStyles()

  const mergedLabels: MobileFeatureToggleRowButtonLabels = {
    enable: "Enable",
    disable: "Disable",
    enabling: "Enabling...",
    disabling: "Disabling...",
  }

  const buttonText = isLoading
    ? enabled
      ? mergedLabels.disabling
      : mergedLabels.enabling
    : enabled
    ? mergedLabels.disable
    : mergedLabels.enable

  return (
    <div className={classes.row}>
      <div className={classes.label}>{label}</div>
      <div className={classes.status}>{enabled ? "Enabled" : "Disabled"}</div>
      <div>
        <div className={classes.actionContainer}>
          <p className={classes.description}>{description}</p>
          <Button
            onClick={() => onSetEnabled(!enabled)}
            disabled={isLoading}
            aria-busy={isLoading}
            customVariant="lite-small"
          >
            {buttonText}
          </Button>
          {!!errorMessage && (
            <span
              className={classes.errorMessage}
              role="alert"
              aria-live="assertive"
            >
              {errorMessage}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  // Matches the 3-column layout used by ClientMobileSettings.
  row: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 3fr",
    columnGap: theme.spacing(3),
    alignItems: "start",
  },
  label: {
    fontWeight: 700,
    alignSelf: "center",
  },
  status: {
    alignSelf: "center",
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(1.5),
    alignItems: "center",
    flexWrap: "wrap",
  },
  description: {
    margin: 0,
    color: colors.neutral.asphalt,
  },
  successMessage: {
    color: colors.trendingData.green.dark,
    fontWeight: 600,
  },
  errorMessage: {
    color: colors.secondary.crimson,
    fontWeight: 600,
  },
}))
